
import { Vue, Component, Watch } from "vue-property-decorator";
import SystemViewAdvancedFiltersApi from "@/services/api.systemViewAdvancedFilters.service";
import {AdvancedStatusFilter, AdvancedStatusFilterRequest} from "@/types/sv-data/system-view/advanced-filters/AdvancedStatusFilter";
import { Getter } from "vuex-class";
import { UserPermissions } from "@/store/types";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { debounce } from "lodash";
import SystemViewAdvancedFilterEdit from "@/components/system-view/advanced-filters/SystemViewAdvancedFilterEdit.vue";

@Component({
	components: {
		SystemViewAdvancedFilterEdit,
		VuePerfectScrollbar: VuePerfectScrollbar
	}
})
export default class SystemViewAdvancedFilters extends Vue {

	@Getter("getPermissions") private permissions: UserPermissions;
	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;
	@Getter("getUserId") private userId: number;

	// noinspection JSMismatchedCollectionQueryUpdate
	private advancedStatusFilters: AdvancedStatusFilter[] = [];
	private searchTerm: string = "";
	private activeFilter: AdvancedStatusFilter = null;
	private editingFilter: AdvancedStatusFilter = null;
	private createFilterModalShow = false;
	private confirmDeleteModalShow = false;
	private filterToDelete: AdvancedStatusFilter = null;
	private dropdownShown: boolean = false;

	private get isAdvancedFilteringEnabled(): boolean {
		return this.getFeature(["SystemView", "AdvancedStatusFilters"])
			&& (this.permissions.canViewAdvancedStatusFilters || this.permissions.canEditAdvancedStatusFilters);
	}

	private async mounted(): Promise<void> {
		await this.getAvailableAdvancedFilters();
	}

	private async getAvailableAdvancedFilters(): Promise<void> {
		if(!this.isAdvancedFilteringEnabled) {
			return;
		}

		let request: AdvancedStatusFilterRequest = {
			searchTerm: this.searchTerm
		};
		this.advancedStatusFilters = await SystemViewAdvancedFiltersApi.getAdvancedStatusFilters(request);
	}

	private async clearActiveFilter(): Promise<void> {
		this.activeFilter = null;
		this.$emit("input", null);
	}

	private async createFilter(): Promise<void> {
		if(!this.isAdvancedFilteringEnabled) {
			return;
		}

		this.editingFilter = null;
		this.createFilterModalShow = true;
	}

	private async editFilter(filter: AdvancedStatusFilter): Promise<void> {
		if(!this.isAdvancedFilteringEnabled) {
			return;
		}

		this.editingFilter = filter;
		this.createFilterModalShow = true;
	}

	private async finishEditFilter(): Promise<void> {
		let selected = this.activeFilter;
		this.createFilterModalShow = false;
		this.editingFilter = null;

		await this.getAvailableAdvancedFilters();

		if(selected !== null) {
			let existing = this.advancedStatusFilters.findIndex((filter: AdvancedStatusFilter) => filter.advancedStatusFilterId == selected.advancedStatusFilterId);
			if(existing !== null) {
				await this.changeActiveFilter(this.advancedStatusFilters[existing]);
			}
			else {
				await this.clearActiveFilter();
			}
		}
	}

	private async confirmDeleteFilter(filter: AdvancedStatusFilter): Promise<void> {
		this.filterToDelete = filter;
		this.confirmDeleteModalShow = true;
	}

	private async deleteFilter(filter: AdvancedStatusFilter): Promise<void> {
		if(!this.isAdvancedFilteringEnabled) {
			return;
		}

		this.confirmDeleteModalShow = false;

		try {
			await SystemViewAdvancedFiltersApi.deleteAdvancedStatusFilter(filter.advancedStatusFilterId);
			if (this.activeFilter?.advancedStatusFilterId === filter.advancedStatusFilterId) {
				await this.clearActiveFilter();
			}
			this.$notify(
				{
					type: "success",
					title: "Advanced Filter Deleted",
					text: "Successfully deleted filter '" + filter.name + "'"
				});
		} catch (e) {
			this.$notify({
				type: "error",
				title: "Error",
				text: "Unable to delete filter - please try again later, or contact support if the problem persists."
			});
		}

		await this.getAvailableAdvancedFilters();

		this.filterToDelete = null;
	}

	@Watch("searchTerm")
	public debouncedFilterSearch: (() => Promise<void>) = debounce(async () => {
		await this.getAvailableAdvancedFilters();
	}, 200);

	private async changeActiveFilter(filter: AdvancedStatusFilter): Promise<void> {
		this.activeFilter = filter;
		this.$emit("input", this.activeFilter?.advancedStatusFilterId);
	}

	private async cancelEditFilter(): Promise<void> {
		this.createFilterModalShow = false;
		this.editingFilter = null;
	}

	private get activeFilterName(): string {
		if (this.activeFilter == null) {
			return "None Selected";
		}
		return this.activeFilter.name;
	}

	private get hasEditFilterPermission(): boolean {
		return this.permissions.canEditAdvancedStatusFilters;
	}

	private canModifyFilter(filter: AdvancedStatusFilter): boolean {
		if (!this.permissions.canEditAdvancedStatusFilters) {
			return false;
		}
		return !(filter.userId !== null && filter.userId != this.userId);
	}
}
